/**
 * custom-gallery.js
 * Trypooh product gallery: thumbnail switching, mobile swipe,
 * and variant→gallery sync via Dawn's PubSub.
 *
 * Dependencies (loaded globally via theme.liquid):
 *   - pubsub.js  (subscribe / publish)
 *   - constants.js  (PUB_SUB_EVENTS)
 */

(function () {
  'use strict';

  class TrypoohGallery {
    /**
     * @param {HTMLElement} galleryEl  – .trypooh-gallery element
     */
    constructor(galleryEl) {
      this.gallery = galleryEl;
      this.sectionId = galleryEl.dataset.section;

      // Parse media array from embedded JSON
      const mediaScriptEl = document.getElementById(`GalleryMedia-${this.sectionId}`);
      this.media = mediaScriptEl ? JSON.parse(mediaScriptEl.textContent.trim()) : [];
      this.count = this.media.length;

      // DOM refs
      this.mainImg = document.getElementById(`GalleryMainImg-${this.sectionId}`);
      this.thumbBtns = Array.from(galleryEl.querySelectorAll('.trypooh-gallery__thumb'));
      this.dots = Array.from(galleryEl.querySelectorAll('.trypooh-gallery__dot'));

      // State
      this.currentIndex = parseInt(galleryEl.dataset.activeIndex || '0', 10);
      this.touchStartX = 0;
      this.touchStartY = 0;

      this._bindEvents();
      this._syncVariantChanges();
    }

    // ── Navigation ──────────────────────────────────────────────────────────

    /**
     * Navigate gallery to a given index.
     * Updates main image src, activates matching thumb and dot.
     * @param {number} index
     */
    goTo(index) {
      if (this.count === 0) return;

      // Wrap around
      index = ((index % this.count) + this.count) % this.count;
      this.currentIndex = index;

      const item = this.media[index];
      if (!item) return;

      // Update main image.
      // srcset must be cleared first — if present, browsers use it for source
      // selection and may ignore a plain .src assignment entirely.
      if (this.mainImg) {
        this.mainImg.removeAttribute('srcset');
        this.mainImg.src = item.src;
        this.mainImg.alt = item.alt || '';
      }

      // Update thumbnails
      this.thumbBtns.forEach((btn, i) => {
        const active = i === index;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', String(active));
      });

      // Update dots
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
      });
    }

    // ── Event binding ────────────────────────────────────────────────────────

    _bindEvents() {
      // Thumbnail clicks
      this.thumbBtns.forEach((btn) => {
        btn.addEventListener('click', () => {
          const idx = parseInt(btn.dataset.index || '0', 10);
          this.goTo(idx);
        });
      });

      // Touch swipe — main gallery (mobile navigation without thumbnails)
      const mainArea = this.gallery.querySelector('.trypooh-gallery__main');
      if (mainArea) {
        mainArea.addEventListener('touchstart', (e) => {
          this.touchStartX = e.changedTouches[0].clientX;
          this.touchStartY = e.changedTouches[0].clientY;
        }, { passive: true });

        mainArea.addEventListener('touchend', (e) => {
          const dx = e.changedTouches[0].clientX - this.touchStartX;
          const dy = Math.abs(e.changedTouches[0].clientY - this.touchStartY);
          if (Math.abs(dx) > 50 && dy < 80) {
            this.goTo(this.currentIndex + (dx < 0 ? 1 : -1));
          }
        }, { passive: true });
      }
    }

    // ── Variant change sync ──────────────────────────────────────────────────

    /**
     * Subscribe to Dawn's variantChange PubSub event.
     * When a variant with a featured_media is selected, navigate the
     * gallery to that image.
     */
    _syncVariantChanges() {
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

      subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        // Only handle events from this section
        if (data.sectionId !== this.sectionId) return;

        const featuredMediaId = data.variant?.featured_media?.id;
        if (!featuredMediaId) return;

        const mediaIndex = this.media.findIndex((m) => m.id === featuredMediaId);
        if (mediaIndex !== -1) {
          this.goTo(mediaIndex);
        }
      });
    }
  }

  // ── Init ────────────────────────────────────────────────────────────────────

  function initGalleries() {
    document.querySelectorAll('.trypooh-gallery').forEach((el) => {
      if (!el.dataset.galleryInit) {
        el.dataset.galleryInit = '1';
        new TrypoohGallery(el);
      }
    });
  }

  // Init on DOMContentLoaded (script is deferred, so DOM is ready)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGalleries);
  } else {
    initGalleries();
  }

  // Re-init after Shopify theme editor section updates
  document.addEventListener('shopify:section:load', initGalleries);

})();
